import { useTranslation } from 'react-i18next';

import { Alert, Collapse } from '@mui/material';
import type { AlertColor } from '@mui/material';

import type { PasskeyEnrolmentOutcome } from '@/shared/auth/passkeyEnrolment';

export interface PasskeyOutcomeAlertProps {
  /** `null` — nothing to show (the common case: most visits consume no marker at all). */
  outcome: PasskeyEnrolmentOutcome | null;
  /** Dismiss for this mount — the marker itself was already removed by the consume-once accessor,
   *  so a reload never brings the alert back regardless of whether this is called. */
  onClose: () => void;
}

// `common` (the default namespace) rather than `account`: these four strings are shared with
// `PasskeyEnrolmentReturn`, which reports the identical outcome to the global snackbar when the
// round trip returns to a page other than this one — one set of copy for one set of outcomes.
const OUTCOME_SEVERITY: Record<PasskeyEnrolmentOutcome, AlertColor> = {
  success: 'success',
  cancelled: 'info',
  error: 'error',
  unknown: 'info',
};

const OUTCOME_MESSAGE_KEY: Record<PasskeyEnrolmentOutcome, string> = {
  success: 'passkeyEnrolment.success',
  cancelled: 'passkeyEnrolment.cancelled',
  error: 'passkeyEnrolment.error',
  unknown: 'passkeyEnrolment.unknown',
};

/**
 * The once-only outcome banner at the top of `/account/security` (design doc §9 states S8–S11,
 * §11 step 5). `error` gets `role="alert"` (assertive — something went wrong); the other three get
 * `role="status"` (polite — informational).
 */
export function PasskeyOutcomeAlert({ outcome, onClose }: PasskeyOutcomeAlertProps) {
  const { t } = useTranslation();

  return (
    <Collapse in={outcome !== null} unmountOnExit>
      {outcome ? (
        <Alert
          severity={OUTCOME_SEVERITY[outcome]}
          role={outcome === 'error' ? 'alert' : 'status'}
          onClose={onClose}
          closeText={t('action.close')}
        >
          {t(OUTCOME_MESSAGE_KEY[outcome])}
        </Alert>
      ) : null}
    </Collapse>
  );
}
